/**
 * Global Leaderboard page.
 *
 * Shows top players for each game, filterable by time period.
 * Fetches from GET /api/leaderboard/global/:gameId?period=today|7d|30d|all
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@playground/ui';
import { apiFetch, apiFetchWithToken } from '@/platform/api.js';
import { usePlayerStore } from '@/store/player.js';

interface GameMeta {
  id: string;
  name: string;
}

interface LeaderboardRow {
  playerId: string;
  displayName: string;
  wins: number;
  losses: number;
  draws: number;
}

const PERIODS = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: '7 Days' },
  { value: '30d', label: '30 Days' },
  { value: 'all', label: 'All Time' },
] as const;

type Period = (typeof PERIODS)[number]['value'];

export function Leaderboard() {
  const [games, setGames] = useState<GameMeta[]>([]);
  const [selectedGame, setSelectedGame] = useState<string>('');
  const [period, setPeriod] = useState<Period>('all');
  const [rows, setRows] = useState<LeaderboardRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  // Subscribe to the token so the games list still loads when auth resolves
  // after this page mounts. Reading it once via getState() left the tabs empty.
  const token = usePlayerStore((s) => s.token);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    apiFetchWithToken('/api/games', token)
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { games?: GameMeta[] } | null) => {
        if (cancelled || !d?.games) return;
        setGames(d.games);
        setSelectedGame((current) => current || d.games?.[0]?.id || '');
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [token]);

  // Fetch leaderboard whenever game or period changes.
  // `cancelled` prevents a slow earlier request from overwriting the rows of a
  // later one when the user switches tabs quickly.
  useEffect(() => {
    if (!selectedGame) return;
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    apiFetch(`/api/leaderboard/global/${selectedGame}?period=${period}`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json() as Promise<{ rows?: LeaderboardRow[] }>;
      })
      .then((d) => { if (!cancelled) setRows(d.rows ?? []); })
      .catch(() => { if (!cancelled) { setRows([]); setFailed(true); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [selectedGame, period, reloadKey]);

  const medals = ['🥇', '🥈', '🥉'];

  /** Dense rank: equal records share a rank rather than being ordered arbitrarily. */
  function rankOf(idx: number): number {
    const row = rows[idx];
    if (!row) return idx + 1;
    for (let i = 0; i < idx; i++) {
      const prev = rows[i]!;
      if (prev.wins === row.wins && prev.losses === row.losses && prev.draws === row.draws) {
        return rankOf(i);
      }
    }
    return idx + 1;
  }

  return (
    <main className="mx-auto max-w-2xl pl-[max(1rem,var(--safe-l))] pr-[max(1rem,var(--safe-r))] pt-[max(3rem,calc(var(--safe-t)+1rem))] pb-12">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2.5 mb-1">
            <img src="/favicon.png" alt="" className="w-8 h-8 rounded-lg flex-shrink-0" />
            <h1 className="truncate text-2xl font-extrabold tracking-tight text-indigo-700 sm:text-3xl dark:text-indigo-400">Leaderboard</h1>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">Top players across all games</p>
        </div>
        <Link
          to="/"
          className="flex min-h-[44px] flex-shrink-0 items-center rounded-lg border border-gray-200 px-3 text-sm text-gray-600 hover:bg-gray-100 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700"
        >
          ← Home
        </Link>
      </div>

      {/* Game tabs */}
      {games.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2">
          {games.map((g) => (
            <button
              key={g.id}
              type="button"
              onClick={() => setSelectedGame(g.id)}
              aria-pressed={selectedGame === g.id}
              className={[
                'rounded-full px-3 py-1 text-sm font-medium transition-colors',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2',
                selectedGame === g.id
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600',
              ].join(' ')}
            >
              {g.name}
            </button>
          ))}
        </div>
      )}

      {/* Period tabs */}
      <div className="mb-6 flex gap-1 rounded-lg bg-gray-100 p-1 w-fit dark:bg-gray-700">
        {PERIODS.map((p) => (
          <button
            key={p.value}
            type="button"
            onClick={() => setPeriod(p.value)}
            aria-pressed={period === p.value}
            className={[
              // py-2, not py-1: at py-1 these are exactly 24px tall, the bare
              // minimum for a tap target and the main filter row on a phone.
              'rounded-md px-3 py-2 text-xs font-semibold transition-colors',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
              period === p.value
                ? 'bg-white text-indigo-700 shadow-sm dark:bg-gray-600 dark:text-indigo-200'
                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200',
            ].join(' ')}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <Card>
        {loading ? (
          <p role="status" className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">Loading…</p>
        ) : failed ? (
          // A failed request used to render as "no results", telling the user
          // there were no players when the API was simply unreachable.
          <div className="py-8 text-center" role="alert">
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Couldn't load the leaderboard</p>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Something went wrong fetching the standings.</p>
            <button
              type="button"
              onClick={() => setReloadKey((k) => k + 1)}
              className="mt-3 rounded-lg bg-indigo-600 px-4 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
            >
              Retry
            </button>
          </div>
        ) : rows.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
            No results yet for this period.
          </p>
        ) : (
          // Six columns plus a free-text player name do not fit a 360px phone.
          // The table scrolls inside its own container rather than pushing the
          // whole page sideways, and keeps a floor width so the numeric columns
          // don't crush into each other.
          <div className="overflow-x-auto">
          <table className="w-full min-w-[26rem] text-sm">
            <caption className="sr-only">
              Top players, {PERIODS.find((p) => p.value === period)?.label}
            </caption>
            <thead>
              <tr className="border-b border-gray-100 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:border-gray-700 dark:text-gray-400">
                <th scope="col" className="pb-2 text-left">#</th>
                <th scope="col" className="pb-2 text-left">Player</th>
                <th scope="col" className="pb-2 text-right">Wins</th>
                <th scope="col" className="pb-2 text-right">Losses</th>
                <th scope="col" className="pb-2 text-right">Draws</th>
                <th scope="col" className="pb-2 text-right">Games</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => {
                const rank = rankOf(idx);
                const medal = medals[rank - 1];
                return (
                  <tr key={row.playerId} className="border-b border-gray-50 last:border-0 dark:border-gray-700">
                    <td className="py-2 pr-3 text-lg">
                      {medal ? (
                        <span title={`Rank ${rank}`}>
                          <span aria-hidden="true">{medal}</span>
                          <span className="sr-only">Rank {rank}</span>
                        </span>
                      ) : (
                        <span className="text-sm tabular-nums text-gray-500 dark:text-gray-400">{rank}</span>
                      )}
                    </td>
                    <th
                      scope="row"
                      title={row.displayName}
                      className="max-w-[9rem] truncate py-2 pr-3 text-left font-medium text-gray-800 dark:text-gray-200"
                    >
                      {row.displayName}
                    </th>
                    <td className="py-2 text-right font-bold tabular-nums text-green-600 dark:text-green-400">{row.wins}</td>
                    <td className="py-2 text-right tabular-nums text-gray-500 dark:text-gray-400">{row.losses}</td>
                    <td className="py-2 text-right tabular-nums text-gray-500 dark:text-gray-400">{row.draws}</td>
                    <td className="py-2 text-right tabular-nums text-gray-600 dark:text-gray-300">
                      {row.wins + row.losses + row.draws}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </Card>
    </main>
  );
}
